<script>
import { mapActions } from 'vuex'

export default {
  name: 'ToolsList',
  components: {
    Action: () => import('@/components/general/Action'),
    Dropdown: () => import('@/components/general/Dropdown'),
    Datatable: () => import('@/components/general/Datatable'),
    FilterList: () => import('@/components/general/FilterList'),
    Pagination: () => import('@/components/general/Pagination'),
    DropdownLink: () => import('@/components/general/DropdownLink'),
    ContentHeader: () => import('@/components/general/ContentHeader'),
    FilterListSearch: () => import('@/components/general/FilterListSearch'),
    Tabs: () => import('@/components/general/Tabs'),
    PageLimitSelector: () => import('@/components/general/PageLimitSelector')
  },

  data () {
    return {
      pagination: {
        query: {},
        filter: {},
        order: {
          active: 'desc',
          title: 'asc'
        },

        page: 1,
        limit: 12,
        lastPage: null
      },

      tabLinks: [
        {
          location: { name: 'tools.index' },
          text: 'Ferramentas'
        },
        {
          location: { name: 'tools.type_tools.index' },
          text: 'Tipos de Ferramentas'
        }
      ]
    }
  },

  computed: {
    typeToolsList () {
      return this.$store.getters.typeToolsList
    },

    canWrite () {
      return this.getContextPermission('tools') === 'write'
    }
  },

  watch: {
    'pagination.page' () {
      this.getTypeToolsList()
    }
  },

  created () {
    this.getTypeToolsList()
  },

  methods: {
    ...mapActions([
      'setFetching',
      'setFeedback',
      'attemptGetTypeToolsList',
      'attemptToggleTypeToolActive'
    ]),

    prevPage () {
      if (this.pagination.page > 1) {
        this.pagination.page -= 1
      }
    },

    nextPage () {
      this.pagination.page += 1
    },

    firstPage () {
      this.pagination.page = 1
    },

    lastPage () {
      this.pagination.page = this.pagination.lastPage
    },

    getTypeToolsList () {
      this.setFetching(true)

      this.attemptGetTypeToolsList(this.pagination).then(({ data }) => {
        this.pagination.lastPage = data.lastPage
      }).finally(() => {
        this.setFetching(false)
      })
    },

    onLimitChange (newLimit) {
      this.pagination.limit = newLimit

      if (this.pagination.page === 1) {
        this.getTypeToolsList()
      } else {
        this.pagination.page = 1
      }
    },

    createNewTypeTool () {
      this.$router.push({ name: 'tools.type_tools.manage' })
    },

    searchTypeToolsPage (search) {
      this.pagination.query = {
        title: search
      }

      if (this.pagination.page === 1) {
        this.getTypeToolsList()
      } else {
        this.pagination.page = 1
      }
    },

    edit (typeToolId) {
      this.$router.push({
        name: 'tools.type_tools.manage',
        params: { id: typeToolId }
      })
    },

    toggleActive (typeTool) {
      this.setFetching(true)

      const data = {}

      data.title = typeTool.title
      data.callText = typeTool.callText
      data.category = typeTool.category || 'Ferramenta Sebrae'
      data.active = !typeTool.active
      data.id = typeTool.id

      this.attemptToggleTypeToolActive(data).then((response) => {
        this.setFeedback({
          message: response.data.active
            ? this.$t('type_tool:feedback.active.success')
            : this.$t('type_tool:feedback.deactivate.success')
        })

        this.getTypeToolsList()
      }).catch(() => {
        this.setFeedback({ message: this.$t('global.error') })
      }).finally(() => {
        this.setFetching(false)
      })
    }
  }
}
</script>

<template>
  <div class="main-content">
    <ContentHeader
      :title="$t('tools:header.title')"
      :description="$t('tools:header.description')"
      background="/assets/images/themes/default/png/partners_banner.png"
    >
      <Tabs
        slot="tabs"
        dark
        :links="tabLinks"
      />
    </ContentHeader>
    <FilterList>
      <Action
        v-if="canWrite"
        slot="button"
        primary
        large
        fixed-width
        type="button"
        text="Novo Tipo de Ferramenta"
        @click="createNewTypeTool"
      />
      <PageLimitSelector
        slot="order"
        :value="pagination.limit"
        storage-key="page_limit_type_tools"
        @input="onLimitChange"
      />
      <FilterListSearch
        slot="search"
        :hide-error-details="true"
        @search="searchTypeToolsPage"
      />
    </FilterList>
    <div
      v-if="typeToolsList.length"
      class="center thematic-pages-table"
    >
      <Datatable :items="typeToolsList">
        <template slot="thead">
          <tr>
            <th class="th">
              {{ $t('type_tools:datatable.header.1') }}
            </th>
            <th
              class="th"
              width="100"
            >
              {{ $t('tools:datatable.header.6') }}
            </th>
            <th
              class="th"
              width="40"
            />
          </tr>
        </template>
        <template
          slot="tbody"
          slot-scope="props"
        >
          <tr>
            <td class="td">
              <span class="td-text bolder">{{ props.item.title }}</span>
            </td>
            <td class="td">
              <span
                v-if="!!props.item.active"
                class="td-tag active-ribbon"
              >{{ $t('global.status:active') }}</span>
              <span
                v-else
                class="td-tag inactive-ribbon"
              >{{ $t('global.status:inactive') }}</span>
            </td>
            <td
              class="td td-actions"
              width="40"
            >
              <Dropdown
                icon="dots-vertical"
                right
              >
                <DropdownLink
                  v-if="canWrite"
                  :text="$t('global:edit')"
                  @click="edit(props.item.id)"
                />
                <DropdownLink
                  v-if="!props.item.active && canWrite"
                  :text="$t('global:activate')"
                  @click="toggleActive(props.item)"
                />
                <DropdownLink
                  v-else-if="canWrite"
                  :text="$t('global:deactivate')"
                  @click="toggleActive(props.item)"
                />
              </Dropdown>
            </td>
          </tr>
        </template>
      </Datatable>
      <Pagination
        :active-page="pagination.page"
        :page-count="pagination.lastPage"
        @firstPage="firstPage"
        @lastPage="lastPage"
        @nextPage="nextPage"
        @previousPage="prevPage"
      />
    </div>
    <div
      v-else
      class="center thematic-pages-table"
    >
      <p>Nenhum tipo ferramenta encontrado para o termo pesquisado</p>
    </div>
  </div>
</template>
